import { formatDate, habilidadeLabels, scoreToLabel, todayISO } from './utils.js';
import { getEvaluationTaxonomy } from '../domain/skills.js';
import { getDiagnosticTask } from '../domain/diagnosticTasks.js';
import { consolidatedAssessment } from './assessmentHistory.js';
import { sessionGeneralObservation, sessionResultSummary } from './session-results.js';
import { caseStudyForStudent } from '../features/case-study/case-study.js';
import { goalReportText, goalTraceability } from '../features/pei/goal-links.js';
import { buildActivityPedagogicalCard } from './activity-pedagogy.js';

const GOAL_STATUS_LABEL = { atingida: 'Meta atingida', no_prazo: 'No ritmo esperado', risco_atraso: 'Risco de atraso', sem_progresso: 'Sem progresso observado', progredindo: 'Progredindo, sem prazo definido', sem_dados: 'Dados insuficientes' };

const safe = (value) => value || '-';
const byDateDesc = (a, b) => String(b.data || '').localeCompare(String(a.data || ''));

async function loadPdfMake() {
  const pdfMakeModule = await import('pdfmake/build/pdfmake');
  const fontsModule = await import('pdfmake/build/vfs_fonts');
  const pdfMake = pdfMakeModule.default || pdfMakeModule;
  const pdfFonts = fontsModule.default || fontsModule;
  pdfMake.vfs = pdfFonts?.pdfMake?.vfs || pdfFonts?.vfs || pdfMake.vfs;
  return pdfMake;
}

async function loadXlsx() {
  return await import('xlsx');
}

function deficits(assessment) {
  return Object.entries(assessment?.pontuacoes || {}).filter(([, value]) => value < 60).sort((a, b) => a[1] - b[1]);
}

function average(assessment) {
  const values = Object.values(assessment?.pontuacoes || {});
  return values.length ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : 0;
}

function schoolOf(data, student) {
  return (data.schools || []).find((school) => school.id === student?.schoolId) || {};
}

function schoolForActivity(data, student, user) {
  return (data.schools || []).find((school) => school.id === (student?.schoolId || user?.schoolId)) || {};
}

function teacherForActivity(data, student, user) {
  if (user?.role === 'professional' || user?.role === 'school_admin') return user;
  const teacherId = student?.profissionais?.[0];
  return (data.users || []).find((item) => item.uid === teacherId) || {};
}

function filename(value) {
  const clean = String(value || 'arquivo')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return clean || 'arquivo';
}

export async function exportActivityPdf(activity, { data, student, user } = {}) {
  const pdfMake = await loadPdfMake();
  const school = schoolForActivity(data || {}, student, user);
  const teacher = teacherForActivity(data || {}, student, user);
  const card = buildActivityPedagogicalCard(activity);
  const listOrDash = (items) => items?.length ? { ul: items } : { text: '-' };
  const document = {
    pageSize: 'A4',
    pageMargins: [40, 44, 40, 44],
    content: [
      { text: 'Atividade pedagógica SGAEE', style: 'title' },
      { text: 'Atendimento Educacional Especializado', style: 'subtitle' },
      { text: 'Identificação', style: 'section' },
      {
        table: {
          widths: ['auto', '*'],
          body: [
            ['Escola', safe(school.nome)],
            ['Professor', safe(teacher.nome)],
            ['Aluno', safe(student?.nome)],
            ['Série/Turma', safe(student?.serie)],
            ['Necessidade educacional', safe(student?.necessidade)],
            ['Data', formatDate(todayISO())]
          ]
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 10]
      },
      { text: safe(activity?.titulo), style: 'activityTitle' },
      { text: 'Objetivo', style: 'section' },
      { text: card.objective },
      { text: 'Quando indicar', style: 'section' },
      { text: card.whenToUse },
      { text: 'Roteiro', style: 'section' },
      card.steps.length ? { ol: card.steps } : { text: '-' },
      { text: 'Como registrar', style: 'section' },
      { text: card.howToRecord },
      { text: 'Adaptação principal', style: 'section' },
      { text: card.mainAdaptation },
      { text: 'Fundamentação e detalhes', style: 'section', pageBreak: 'before' },
      { text: 'Materiais', style: 'subsection' },
      listOrDash(card.materials),
      { text: 'Por que funciona', style: 'subsection' },
      { text: card.whyWorks },
      { text: 'Pré-requisitos', style: 'subsection' },
      listOrDash(card.prerequisites),
      { text: 'Simplificar', style: 'subsection' },
      { text: card.simplify },
      { text: 'Progredir', style: 'subsection' },
      { text: card.progress },
      { text: 'Sinais de inadequação', style: 'subsection' },
      listOrDash(card.inadequacySigns),
      { text: 'Erros comuns', style: 'subsection' },
      listOrDash(card.commonErrors),
      { text: 'Generalização', style: 'subsection' },
      { text: card.generalization },
      { text: 'Critério de domínio', style: 'subsection' },
      { text: card.criterion },
      { text: 'Autoria, fonte e validação', style: 'section' },
      { text: `Autoria/curadoria: ${card.authorship}` },
      { text: `Fonte de referência: ${card.source}` },
      { text: `Situação de validação: ${card.validation}` },
      { text: 'Uma referência bibliográfica não comprova autoria ou validação específica da atividade.', style: 'note' },
      { text: 'Documento pedagógico de apoio ao AEE. Ajuste a atividade conforme observação direta, perfil funcional do estudante e planejamento do professor.', style: 'note' }
    ],
    styles: {
      title: { fontSize: 18, bold: true, margin: [0, 0, 0, 4] },
      subtitle: { fontSize: 11, color: '#64748b', margin: [0, 0, 0, 14] },
      section: { fontSize: 13, bold: true, margin: [0, 12, 0, 6] },
      subsection: { fontSize: 11, bold: true, margin: [0, 9, 0, 4] },
      activityTitle: { fontSize: 15, bold: true, margin: [0, 8, 0, 6] },
      note: { fontSize: 9, color: '#64748b', margin: [0, 8, 0, 0] }
    }
  };

  const schoolName = filename(school.nome || 'Escola');
  const studentName = filename(student?.nome || 'Aluno');
  const activityName = filename(activity?.titulo || activity?.id || 'Atividade');
  pdfMake.createPdf(document).download(`Atividade_SGAEE_${schoolName}_${studentName}_${activityName}.pdf`);
}

/**
 * Exporta uma "atividade pronta" (ficha de uma página, sem preparo de material) já
 * formatada para impressão imediata: cabeçalho com aluno/data, instrução curta para o
 * professor e o conteúdo imprimível (traçado, contagem, tabela etc.) gerado por
 * buildWorksheetBody. Pensada para o professor imprimir e aplicar na hora.
 */
export async function exportAtividadeProntaPdf(atividade, { student, data } = {}) {
  const { buildWorksheetBody } = await import('./printableWorksheet.js');
  const pdfMake = await loadPdfMake();
  const school = schoolOf(data || {}, student);
  const document = {
    pageSize: 'A4',
    pageMargins: [40, 44, 40, 44],
    content: [
      { text: 'Atividade pronta SGAEE', style: 'title' },
      { text: 'Ficha de uma página, pronta para aplicar', style: 'subtitle' },
      {
        table: {
          widths: ['auto', '*'],
          body: [
            ['Aluno', safe(student?.nome)],
            ['Escola', safe(school.nome)],
            ['Data', formatDate(todayISO())],
            ['Habilidade(s)', (atividade?.habilidades_alvo || []).map((id) => habilidadeLabels[id] || id).join(', ') || '-'],
          ],
        },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 12],
      },
      { text: safe(atividade?.titulo), style: 'activityTitle' },
      { text: safe(atividade?.objetivo), margin: [0, 0, 0, 6] },
      { text: `Como aplicar: ${safe(atividade?.instrucao)}`, italics: true, margin: [0, 0, 0, 14] },
      ...buildWorksheetBody(atividade),
      { text: 'Ficha pedagógica de apoio ao AEE. Ajuste conforme a observação direta e o perfil funcional do estudante.', style: 'note' },
    ],
    styles: {
      title: { fontSize: 18, bold: true, margin: [0, 0, 0, 4] },
      subtitle: { fontSize: 11, color: '#64748b', margin: [0, 0, 0, 14] },
      activityTitle: { fontSize: 16, bold: true, margin: [0, 4, 0, 6] },
      note: { fontSize: 9, color: '#64748b', margin: [0, 14, 0, 0] },
    },
  };
  const studentName = filename(student?.nome || 'Aluno');
  const activityName = filename(atividade?.titulo || atividade?.id || 'Atividade');
  pdfMake.createPdf(document).download(`Atividade_Pronta_SGAEE_${studentName}_${activityName}.pdf`);
}

/**
 * Protocolo em branco da avaliação diagnóstica inicial: uma tarefa de observação por
 * habilidade, organizada por eixo, com a opção "não observado" separada da escala
 * numérica. Foi pensado para uso sem notebook durante a observação direta do aluno,
 * com apenas os escores efetivamente observados transcritos depois na aba "Avaliação".
 */
export async function exportDiagnosticProtocolPdf(student) {
  const pdfMake = await loadPdfMake();
  const eixos = getEvaluationTaxonomy();
  const document = {
    pageSize: 'A4',
    pageMargins: [40, 44, 40, 44],
    content: [
      { text: 'Protocolo de avaliação diagnóstica inicial', style: 'title' },
      { text: 'Atendimento Educacional Especializado — SGAEE', style: 'subtitle' },
      {
        table: { widths: ['auto', '*'], body: [['Aluno', safe(student?.nome)], ['Série/turma', safe(student?.serie)], ['Data da observação', '____/____/______'], ['Observador(a)', '________________________________']] },
        layout: 'lightHorizontalLines',
        margin: [0, 0, 0, 12],
      },
      { text: 'Escala: N/O = não observado · 0 = ainda não demonstrado · 40 = apoio intensivo · 60 = em desenvolvimento · 80 = consolidado · 100 = independente/generalizado.', italics: true, margin: [0, 0, 0, 12] },
      ...eixos.flatMap((eixo) => [
        { text: eixo.nome, style: 'section' },
        ...eixo.habilidades.flatMap((habilidade) => [
          { text: habilidade.nome, bold: true, margin: [0, 8, 0, 2] },
          { text: `Tarefa: ${getDiagnosticTask(habilidade.id)}`, margin: [0, 0, 0, 4] },
          { columns: [{ width: '*', text: 'Marque: N/O ☐   0 ☐   40 ☐   60 ☐   80 ☐   100 ☐' }, { width: '*', text: 'Observações: __________________________' }], columnGap: 10 },
        ]),
      ]),
      { text: 'Após a observação, transcreva somente as habilidades que receberam pontuação. Itens N/O permanecem como não observados e não entram na média.', style: 'note' },
    ],
    styles: {
      title: { fontSize: 18, bold: true, margin: [0, 0, 0, 4] },
      subtitle: { fontSize: 11, color: '#64748b', margin: [0, 0, 0, 14] },
      section: { fontSize: 13, bold: true, margin: [0, 14, 0, 4], color: '#4f46e5' },
      note: { fontSize: 9, color: '#64748b', margin: [0, 16, 0, 0] },
    },
  };
  pdfMake.createPdf(document).download(`Protocolo_Diagnostico_Inicial_SGAEE_${filename(student?.nome || 'Aluno')}.pdf`);
}

export async function exportStudentPdf(student, data) {
  const pdfMake = await loadPdfMake();
  const school = schoolOf(data, student);
  const assessment = consolidatedAssessment(data.assessments || [], student.id);
  const logs = (data.logs || []).filter((item) => item.studentId === student.id).sort(byDateDesc);
  const pei = (data.peis || []).find((item) => item.studentId === student.id);
  const caseStudy = caseStudyForStudent(data.caseStudies, student.id);
  const consent = (data.consents || []).find((item) => item.studentId === student.id && item.ativo);
  const priority = deficits(assessment).slice(0, 8);

  const document = {
    pageSize: 'A4',
    pageMargins: [40, 48, 40, 48],
    content: [
      { text: 'Relatório pedagógico SGAEE', style: 'title' },
      { text: 'Atendimento Educacional Especializado', style: 'subtitle' },
      { text: 'Identificação da escola', style: 'section' },
      { text: `Escola: ${safe(school.nome)}` },
      { text: `CNPJ: ${safe(school.cnpj)} | Cidade/UF: ${safe(school.cidade)}/${safe(school.estado)}` },
      { text: `Endereço: ${safe(school.endereco)}` },
      { text: `Contato: ${safe(school.telefone)} | ${safe(school.email)}` },
      { text: `Direção/coordenação AEE: ${safe(school.diretor)} | ${safe(school.coordenadorAee)}` },
      { text: `Responsável institucional: ${safe(school.responsavelNome)} | ${safe(school.responsavelEmail)} | ${safe(school.responsavelTelefone)}`, margin: [0, 0, 0, 12] },
      { text: `Estudante: ${safe(student.nome)}`, style: 'section' },
      { text: `Série/Turma: ${safe(student.serie)} | Necessidade: ${safe(student.necessidade)}` },
      { text: `Entrada: ${formatDate(student.entrada)} | Última revisão: ${formatDate(student.ultimaRevisao)}` },
      { text: `Consentimento: ${consent ? `registrado em ${formatDate(consent.data)}` : 'pendente'}`, margin: [0, 0, 0, 12] },
      { text: 'Síntese', style: 'section' },
      { text: `Média global: ${assessment ? `${average(assessment)}%` : '-'}` },
      { text: `Atendimentos registrados: ${logs.length}` },
      { text: `Metas PEI/PAEE: ${pei?.metas?.length || 0}`, margin: [0, 0, 0, 12] },
      { text: 'Prioridades identificadas', style: 'section' },
      priority.length ? { ul: priority.map(([id, value]) => `${habilidadeLabels[id] || id}: ${scoreToLabel(value)} (${value}/100)`) } : { text: 'Sem prioridade abaixo do limiar.' },
      { text: 'Metas PEI/PAEE', style: 'section' },
      pei?.metas?.length ? { ul: pei.metas.map((meta) => goalReportText(meta, { caseStudy, logs })) } : { text: 'Sem metas registradas.' },
      { text: 'Atendimentos e atividades aplicadas', style: 'section' },
      logs.length ? {
        table: {
          widths: ['auto', '*'],
          body: [['Data', 'Registro'], ...logs.map((log) => [formatDate(log.data), `${safe(log.objetivo)}\n${safe(log.estrategia)}\n${safe(sessionGeneralObservation(log))}\n${sessionResultSummary(log)}`])]
        },
        layout: 'lightHorizontalLines'
      } : { text: 'Sem atendimentos registrados.' },
      { text: 'Documento pedagógico de apoio ao AEE. Não substitui avaliação clínica, parecer multiprofissional ou decisão pedagógica do professor.', style: 'note' }
    ],
    styles: {
      title: { fontSize: 18, bold: true, margin: [0, 0, 0, 4] },
      subtitle: { fontSize: 11, color: '#64748b', margin: [0, 0, 0, 14] },
      section: { fontSize: 13, bold: true, margin: [0, 12, 0, 6] },
      note: { fontSize: 9, color: '#64748b', margin: [0, 18, 0, 0] }
    }
  };

  pdfMake.createPdf(document).download(`Relatorio_SGAEE_${student.nome.replace(/\s+/g, '_')}.pdf`);
}

export async function exportStudentWorkbook(student, data) {
  const XLSX = await loadXlsx();
  const school = schoolOf(data, student);
  const assessment = consolidatedAssessment(data.assessments || [], student.id);
  const logs = (data.logs || []).filter((item) => item.studentId === student.id).sort(byDateDesc);
  const pei = (data.peis || []).find((item) => item.studentId === student.id);
  const caseStudy = caseStudyForStudent(data.caseStudies, student.id);
  const priority = deficits(assessment);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet([{ ...school }]), 'Escola');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet([{ ...student }]), 'Aluno');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(priority.map(([id, value]) => ({ habilidade: habilidadeLabels[id] || id, pontuacao: value, nivel: scoreToLabel(value) }))), 'Prioridades');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet((pei?.metas || []).map((meta) => {
    const trace = goalTraceability(meta, { caseStudy, logs });
    const evidence = trace.evidence.items[0];
    return {
      barreira: trace.barrier,
      origemBarreira: trace.barrierSource,
      habilidade: trace.skills.map((id) => habilidadeLabels[id] || id).join(', '),
      meta: meta.descricao,
      linhaBase: meta.linha_base,
      alvo: meta.alvo_numerico,
      atividadeAplicada: trace.evidence.activities.join(', '),
      evidenciaRecente: evidence ? `${formatDate(evidence.date)} — ${evidence.result}` : 'sem evidência relacionada',
      proximaRevisao: meta.prazo,
      status: meta.status
    };
  })), 'PEI');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(logs.map((log) => ({ data: log.data, objetivo: log.objetivo, estrategia: log.estrategia, observacao: sessionGeneralObservation(log), resultadoApoio: sessionResultSummary(log), atividadeId: log.atividadeId }))), 'Atendimentos');
  XLSX.writeFile(workbook, `Relatorio_SGAEE_${student.nome.replace(/\s+/g, '_')}.xlsx`);
}

export async function exportFullBackupWorkbook(data) {
  const XLSX = await loadXlsx();
  const workbook = XLSX.utils.book_new();
  for (const [name, rows] of Object.entries(data)) {
    if (Array.isArray(rows)) XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), name.slice(0, 31));
  }
  XLSX.writeFile(workbook, `Backup_SGAEE_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

function reportStudentSection(report) {
  const { student, school, summary, alerts, goals, logs, consent } = report;
  return [
    { text: student.nome, style: 'activityTitle', pageBreak: 'before' },
    { text: `${safe(student.serie)} · ${safe(student.necessidade)} · escola ${safe(school.nome)}`, margin: [0, 0, 0, 8] },
    { text: `Média atual: ${summary.currentAverage}% (variação de ${summary.deltaAverage >= 0 ? '+' : ''}${summary.deltaAverage} pontos) · ${logs.length} atendimento(s) · consentimento ${consent ? `registrado em ${formatDate(consent.data)}` : 'pendente'}.`, margin: [0, 0, 0, 8] },
    { text: 'Prioridades atuais', style: 'section' },
    summary.priorities.length ? { ul: summary.priorities.map((item) => `${habilidadeLabels[item.skill] || item.skill}: ${scoreToLabel(item.now)} (${item.now}/100)`) } : { text: 'Sem prioridade identificada.' },
    { text: 'Alertas de acompanhamento', style: 'section' },
    alerts.length ? { ul: alerts.map((alert) => alert.mensagem) } : { text: 'Nenhum alerta no momento.' },
    { text: 'Metas do PEI/PAEE', style: 'section' },
    goals.length ? { ul: goals.map(({ meta, projecao }) => `${meta.descricao} — ${GOAL_STATUS_LABEL[projecao.status] || projecao.status}`) } : { text: 'Sem metas registradas.' },
  ];
}

export async function exportReportBlockPdf(reportBlock) {
  const pdfMake = await loadPdfMake();
  const document = {
    pageSize: 'A4',
    pageMargins: [40, 48, 40, 48],
    content: [
      { text: 'Bloco de relatórios pedagógicos SGAEE', style: 'title' },
      { text: reportBlock.nome, style: 'subtitle' },
      { text: 'Síntese do grupo', style: 'section' },
      { text: `${reportBlock.totalAlunos} estudante(s) · média geral ${reportBlock.groupAverage}% · ${reportBlock.alertingCount} em alerta · ${reportBlock.pendingConsent} consentimento(s) pendente(s).`, margin: [0, 0, 0, 8] },
      reportBlock.groupAverageBySkill.length ? { ul: reportBlock.groupAverageBySkill.slice(0, 8).map((item) => `${habilidadeLabels[item.skill] || item.skill}: média ${item.media}/100 (n=${item.amostra})`) } : { text: 'Sem avaliações suficientes para média por habilidade.' },
      ...reportBlock.reports.flatMap(reportStudentSection),
      { text: 'Documento pedagógico de apoio ao AEE. Não substitui avaliação clínica, parecer multiprofissional ou decisão pedagógica do professor.', style: 'note', pageBreak: 'before' },
    ],
    styles: {
      title: { fontSize: 18, bold: true, margin: [0, 0, 0, 4] },
      subtitle: { fontSize: 11, color: '#64748b', margin: [0, 0, 0, 14] },
      section: { fontSize: 13, bold: true, margin: [0, 12, 0, 6] },
      activityTitle: { fontSize: 15, bold: true, margin: [0, 8, 0, 6] },
      note: { fontSize: 9, color: '#64748b', margin: [0, 8, 0, 0] },
    },
  };
  pdfMake.createPdf(document).download(`Bloco_Relatorios_SGAEE_${filename(reportBlock.nome)}.pdf`);
}

export async function exportReportBlockWorkbook(reportBlock) {
  const XLSX = await loadXlsx();
  const workbook = XLSX.utils.book_new();
  const resumoRows = reportBlock.reports.map(({ student, school, summary, alerts, logs, consent }) => ({
    aluno: student.nome,
    escola: school.nome,
    serie: student.serie,
    necessidade: student.necessidade,
    mediaAtual: summary.currentAverage,
    variacao: summary.deltaAverage,
    atendimentos: logs.length,
    alertas: alerts.length,
    consentimento: consent ? `registrado em ${formatDate(consent.data)}` : 'pendente',
  }));
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet([
    { bloco: reportBlock.nome, geradoEm: formatDate(reportBlock.geradoEm.slice(0, 10)), totalAlunos: reportBlock.totalAlunos, mediaGeral: reportBlock.groupAverage, emAlerta: reportBlock.alertingCount, consentimentosPendentes: reportBlock.pendingConsent },
  ]), 'Sintese');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(resumoRows), 'Alunos');
  const usedNames = new Set();
  for (const report of reportBlock.reports) {
    const rows = [
      ['Prioridades pedagógicas'],
      ['Habilidade', 'Atual', 'Variação'],
      ...report.summary.priorities.map((item) => [habilidadeLabels[item.skill] || item.skill, item.now, item.delta]),
      [],
      ['Atendimentos'],
      ['Data', 'Objetivo', 'Estratégia', 'Observação', 'Resultado e apoio'],
      ...report.logs.map((log) => [log.data, log.objetivo, log.estrategia, sessionGeneralObservation(log), sessionResultSummary(log)]),
    ];
    let sheetName = filename(report.student.nome).slice(0, 28) || 'Aluno';
    while (usedNames.has(sheetName)) sheetName = `${sheetName.slice(0, 25)}_${usedNames.size}`;
    usedNames.add(sheetName);
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows), sheetName);
  }
  XLSX.writeFile(workbook, `Bloco_Relatorios_SGAEE_${filename(reportBlock.nome)}.xlsx`);
}
